import React, { useEffect, useState } from 'react';

import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';

import { CellValueChangedEvent, ColDef, GridApi } from 'ag-grid-community';
import { BaseAgGrid } from 'src/components/BaseAgGrid';
import { GLOBAL_TERRITORY } from 'src/constants/context';
import { AppContext } from 'src/contexts/appContext';

import {
    AddRowParams,
    CellValueChangeParams,
    DeleteRowsParams,
    handleAddRow,
    handleCellValueChange,
    handleDeleteRows
} from './TerritoryBasedGridLogic';

export const DEFAULT_EMPTY_TERRITORY = '  ';
export const TERRITORY_IS_REQUIRED = 'Territory is Required for New Row';

export interface TerritoryBasedGridConfig {
    // Data configuration
    contractTermKey: string; // e.g., 'thirdPartyDeductionCaps' or 'streamingTaxDeductionCaps'
    dataPath?: string; // e.g., 'territoryOverrides' for nested data, empty for direct array

    columns: ColDef[];

    // Row creation
    createNewRow: () => object;

    // UI configuration
    subheading?: string;
    showPagination?: boolean;
    paginationPageSize?: number;
    gridClassName?: string;
}

export interface TerritoryBasedGridProps {
    config: TerritoryBasedGridConfig;
    contractTerms: any;
    isEditMode: boolean;
    handleContractTermValueChange: (sectionKey: string, key: string, value: any) => void;
    handleContractTermErrorMessageChange?: (sectionKey: string, key: string, value: string) => void;
    contractTermErrorMessages?: Record<string, Record<string, string>>;
}

export const TerritoryBasedGrid: React.FC<TerritoryBasedGridProps> = ({
    config,
    contractTerms,
    isEditMode,
    handleContractTermValueChange,
    handleContractTermErrorMessageChange,
    contractTermErrorMessages
}) => {
    const { metadata } = React.useContext(AppContext);
    const [agGridApi, setAgGridApi] = useState<GridApi>();

    const {
        contractTermKey,
        dataPath,
        columns,
        createNewRow,
        subheading,
        showPagination = false,
        paginationPageSize = 30,
        gridClassName
    } = config;

    // Get the data array from contract terms
    const getData = () => {
        const contractData = contractTerms[contractTermKey];
        if (dataPath) {
            return contractData?.[dataPath] || [];
        }
        return contractData || [];
    };

    // Update the data array in contract terms
    const updateData = (newData: any[]) => {
        if (dataPath) {
            handleContractTermValueChange(contractTermKey, dataPath, newData);
        } else {
            handleContractTermValueChange(contractTermKey, '', newData);
        }
    };

    // Territory filtering logic
    const territoryCodeList: string[] = [];
    let territoriesInUse: string[] = [];

    const territories = Object.values(metadata.territoryMapping).filter(
        (territory) => territory.territoryCode !== GLOBAL_TERRITORY.NAME
    );

    useEffect(() => {
        const data = getData();
        if (data) {
            territoriesInUse = data.map((item: any) => item.territory!.toString());
        }
        territories.map((territory) => {
            if (!territoriesInUse.includes(territory.territoryCode!)) {
                territoryCodeList.push(territory.territoryCode!.toString());
            }
        });
    }, [territoryCodeList]);

    // Error handling
    const handleSingleErrorMsgChange = (field: string, value: string) => {
        if (!handleContractTermErrorMessageChange) return;
        handleContractTermErrorMessageChange(contractTermKey, field, value);
    };

    const onCellValueChanged = (event: CellValueChangedEvent) => {
        const params: CellValueChangeParams = {
            event,
            getData,
            updateData,
            enhancedColumns,
            contractTermKey,
            contractTermErrorMessages,
            handleSingleErrorMsgChange
        };

        handleCellValueChange(params);
    };

    const onClickAddRow = () => {
        const params: AddRowParams = {
            agGridApi,
            getData,
            updateData,
            createNewRow,
            contractTermKey,
            contractTermErrorMessages,
            handleSingleErrorMsgChange
        };

        handleAddRow(params);
    };

    const onClickDeleteRows = () => {
        const params: DeleteRowsParams = {
            agGridApi,
            getData,
            updateData,
            contractTermKey,
            contractTermErrorMessages,
            handleSingleErrorMsgChange
        };

        handleDeleteRows(params);
    };

    // Enhance columns with territory dropdown
    const enhancedColumns = columns.map((col) => {
        if (col.field === 'territory') {
            return {
                ...col,
                cellEditorParams: { values: territoryCodeList }
            };
        }
        return col;
    });

    return (
        <Column spacing="400">
            {subheading && <Heading level={6}>{subheading}</Heading>}

            {isEditMode && (
                <Column>
                    <Row>
                        <Button onClick={onClickAddRow} type="tertiary" size="small">
                            Add a row
                        </Button>
                        <Button onClick={onClickDeleteRows} type="tertiary" size="small">
                            Delete selected rows
                        </Button>
                    </Row>
                </Column>
            )}

            <Row className={gridClassName}>
                <BaseAgGrid
                    columnDefs={enhancedColumns}
                    rowData={getData()}
                    setAgGridApi={setAgGridApi}
                    suppressRowClickSelection={true}
                    onCellValueChanged={onCellValueChanged}
                    stopEditingWhenCellsLoseFocus={true}
                    pagination={showPagination}
                    paginationPageSize={paginationPageSize}
                    domLayout={'normal'}
                />
            </Row>

            <Row>
                {contractTermErrorMessages?.[contractTermKey]?.table && (
                    <Alert size="small" type="error">
                        {contractTermErrorMessages[contractTermKey].table}
                    </Alert>
                )}
            </Row>
        </Column>
    );
};
